import { z } from 'zod';
import { MetadataSchema } from '../common/index.js';
export const TenantRoleSchema = z.enum(['admin', 'member', 'viewer']);
export const UserStatusSchema = z.enum([
    'active',
    'inactive',
    'suspended',
    'pending',
]);
export const EnvironmentTypeSchema = z.enum([
    'development',
    'staging',
    'production',
]);
export const LoginSchema = z.object({
    email: z
        .string()
        .email('Invalid email address')
        .transform(val => val.toLowerCase().trim()),
    password: z.string().min(8, 'Password must be at least 8 characters'),
});
export const SignupSchema = z.object({
    email: z
        .string()
        .email('Invalid email address')
        .transform(val => val.toLowerCase().trim()),
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
});
export const SignupFormSchema = z
    .object({
    email: z
        .string()
        .email('Invalid email address')
        .transform(val => val.toLowerCase().trim()),
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
})
    .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
});
export const ForgotPasswordSchema = z.object({
    email: z.string().email('Invalid email address'),
});
export const ResetPasswordSchema = z.object({
    token: z.string().min(1, 'Token is required'),
    new_password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
});
export const VerifyEmailSchema = z.object({
    token: z.string().min(1, 'Verification token is required'),
});
export const ChangePasswordSchema = z.object({
    old_password: z.string().min(1, 'Current password is required'),
    new_password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
});
export const RefreshTokenSchema = z.object({
    refresh_token: z.string().min(1, 'Refresh token is required'),
});
export const OAuthSyncSchema = z.object({
    access_token: z.string(),
    refresh_token: z.string().optional(),
});
export const AuthUserSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string().nullable(),
    role: TenantRoleSchema,
    tenant_id: z.string(),
    avatar_url: z.string().url().nullable().optional(),
    status: UserStatusSchema.optional(),
    metadata: MetadataSchema.optional(),
});
export const AuthTenantSchema = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    user_role: TenantRoleSchema,
});
export const AuthEnvironmentSchema = z.object({
    id: z.string(),
    name: z.string(),
    type: EnvironmentTypeSchema,
});
export const AuthTokensSchema = z.object({
    access_token: z.string(),
    refresh_token: z.string(),
    expires_in: z.number(),
    token_type: z.literal('bearer'),
});
export const LoginResponseSchema = z.object({
    user: AuthUserSchema,
    tokens: AuthTokensSchema,
    tenant: AuthTenantSchema,
    environment: AuthEnvironmentSchema.optional(),
    message: z.string(),
});
export const SignupResponseSchema = z.object({
    user: AuthUserSchema,
    tokens: AuthTokensSchema.optional(),
    tenant: AuthTenantSchema,
    environment: AuthEnvironmentSchema.optional(),
    message: z.string(),
});
export const RefreshTokenResponseSchema = z.object({
    access_token: z.string(),
    refresh_token: z.string(),
    expires_in: z.number(),
    token_type: z.literal('bearer'),
    user: AuthUserSchema.optional(),
});
export const CurrentUserResponseSchema = z.object({
    user: AuthUserSchema,
    tenant: AuthTenantSchema.nullable(),
    environment: AuthEnvironmentSchema.nullable(),
});
export const SessionSchema = z.object({
    user: AuthUserSchema,
    tenant: AuthTenantSchema.nullable(),
    environment: AuthEnvironmentSchema.nullable(),
    tokens: AuthTokensSchema,
});
export const AuthErrorCodeSchema = z.enum([
    'INVALID_CREDENTIALS',
    'USER_NOT_FOUND',
    'EMAIL_ALREADY_EXISTS',
    'INVALID_TOKEN',
    'TOKEN_EXPIRED',
    'REFRESH_TOKEN_INVALID',
    'UNAUTHORIZED',
    'FORBIDDEN',
    'VALIDATION_ERROR',
    'RATE_LIMITED',
    'SERVER_ERROR',
]);
export const AuthErrorResponseSchema = z.object({
    error: z.object({
        code: AuthErrorCodeSchema,
        message: z.string(),
        details: z.record(z.string(), z.unknown()).optional(),
    }),
    request_id: z.string(),
});
export const ForgotPasswordResponseSchema = z.object({
    message: z.string(),
    email: z.string().email(),
});
export const ResetPasswordResponseSchema = z.object({
    message: z.string(),
    success: z.boolean(),
});
export const VerifyEmailResponseSchema = z.object({
    message: z.string(),
    success: z.boolean(),
    user: AuthUserSchema.optional(),
});
export const ChangePasswordResponseSchema = z.object({
    message: z.string(),
    success: z.boolean(),
});
export const AcceptInvitationSchema = z.object({
    invitation_token: z.string().min(1, 'Invitation token is required'),
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .optional(),
    name: z.string().min(2).max(100).optional(),
});
export const AcceptInvitationResponseSchema = z.object({
    success: z.boolean(),
    user: AuthUserSchema,
    tenant_info: z.object({
        id: z.string(),
        name: z.string(),
        slug: z.string(),
    }),
    accepted_at: z.string().datetime(),
});
export const InvitationResponseSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    role: TenantRoleSchema,
    token: z.string(),
    expires_at: z.string().datetime(),
    invite_url: z.string().url(),
    invited_by: z.object({
        id: z.string(),
        name: z.string().nullable(),
        email: z.string().email(),
    }),
    created_at: z.string().datetime(),
});
//# sourceMappingURL=index.js.map